// Producer take review queue.
//
// Realtime: subscribes to org:<id>:takes; new takes pop in via cache
// invalidation. Bulk approve from the queue toolbar.

import { copy } from '@ohear/shared'
import { Button, Card, EmptyState, Modal, Spinner, Textarea } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { QcMetrics, TakeAudioPlayer, TakeReviewRow } from '../../../features/takes-review'
import { useProducerProject } from '../../../lib/api/producer-projects'
import {
  reviewQueueKey,
  useBulkApproveTakes,
  useDecideTake,
  useReviewQueue,
  useReviewTake,
} from '../../../lib/api/producer-takes'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'
import { useProducerTakesChannel } from '../../../lib/realtime-projects'
import { useRealtimeChannel } from '../../../lib/realtime/use-channel'

export const Route = createFileRoute('/producer/projects/$projectId/takes')({
  component: TakesPage,
})

function TakesPage() {
  useRequireRole(['owner', 'member'])
  const t = useT()
  const { projectId } = Route.useParams()
  const projectQ = useProducerProject(projectId)
  const orgId = projectQ.data?.organization_id ?? null
  useProducerTakesChannel(orgId, projectId)
  // Generalised takes-topic subscription. Supplements the dedicated takes
  // channel by also catching ad-hoc broadcast events (e.g. actor presence)
  // and refreshing the review queue when any TakeUploaded / TakeApproved /
  // TakeRejected event lands in domain_events.
  useRealtimeChannel({
    orgId,
    topic: 'takes',
    invalidateKeys: [reviewQueueKey(projectId)],
  })

  const queueQ = useReviewQueue(projectId)
  const decide = useDecideTake(projectId)
  const bulkApprove = useBulkApproveTakes(projectId)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [openTakeId, setOpenTakeId] = useState<string | null>(null)
  const [pickupNote, setPickupNote] = useState('')
  const [pickupForId, setPickupForId] = useState<string | null>(null)

  const takeQ = useReviewTake(openTakeId ?? undefined)

  const queue = queueQ.data ?? []

  const onSelect = (id: string, next: boolean) => {
    setSelected((prev) => {
      const copy = new Set(prev)
      if (next) copy.add(id)
      else copy.delete(id)
      return copy
    })
  }

  const onApprove = (takeId: string) =>
    void decide.mutateAsync({ take_id: takeId, decision: 'approved', supervisor_note: null })
  const onArchive = (takeId: string) =>
    void decide.mutateAsync({ take_id: takeId, decision: 'archived', supervisor_note: null })

  const onSubmitPickup = async () => {
    if (!pickupForId) return
    await decide.mutateAsync({
      take_id: pickupForId,
      decision: 'rejected_pickup',
      supervisor_note: pickupNote.trim() || null,
    })
    setPickupForId(null)
    setPickupNote('')
  }

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[var(--border)]">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">
            Take review
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
            {projectQ.data?.name ?? ''}
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            <span className="tabular-nums font-medium text-[var(--text)]">{queue.length}</span> take
            {queue.length === 1 ? '' : 's'} pending decision.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-[var(--text-2)] tabular-nums">
            {selected.size} selected
          </span>
          <Button
            variant="primary"
            disabled={selected.size === 0 || bulkApprove.isPending}
            loading={bulkApprove.isPending}
            onClick={async () => {
              await bulkApprove.mutateAsync({ take_ids: [...selected] })
              setSelected(new Set())
            }}
          >
            Bulk approve
          </Button>
        </div>
      </header>

      {queueQ.isLoading ? (
        <Card>
          <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
            <Spinner /> Loading queue…
          </div>
        </Card>
      ) : queue.length === 0 ? (
        <EmptyState
          title="Queue is empty"
          description="New takes drop in here as actors finish recording."
        />
      ) : (
        <Card dense>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className="sticky top-0 z-10" style={{ background: 'var(--bg-3)' }}>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                  <th className="w-6 px-2 py-2 font-medium" />
                  <th className="px-2 py-2 font-medium">Char</th>
                  <th className="px-2 py-2 font-medium">Cue</th>
                  <th className="px-2 py-2 font-medium">Line</th>
                  <th className="px-2 py-2 font-medium">Waveform</th>
                  <th className="px-2 py-2 font-medium">QC</th>
                  <th className="px-2 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((take) => (
                  <TakeReviewRow
                    key={take.id}
                    take={take}
                    selected={selected.has(take.id)}
                    onSelect={(n) => onSelect(take.id, n)}
                    onOpen={() => setOpenTakeId(take.id)}
                    onApprove={() => onApprove(take.id)}
                    onPickup={() => {
                      setPickupForId(take.id)
                      setPickupNote('')
                    }}
                    onArchive={() => onArchive(take.id)}
                    busy={decide.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal
        open={openTakeId != null}
        onClose={() => setOpenTakeId(null)}
        title={
          takeQ.data ? `Take · ${takeQ.data.character_code} #${takeQ.data.cue_number}` : 'Take'
        }
        size="lg"
        footer={
          openTakeId ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  if (openTakeId) onArchive(openTakeId)
                  setOpenTakeId(null)
                }}
              >
                Archive
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPickupForId(openTakeId)
                  setOpenTakeId(null)
                }}
              >
                Pickup
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (openTakeId) onApprove(openTakeId)
                  setOpenTakeId(null)
                }}
              >
                Approve
              </Button>
            </>
          ) : null
        }
      >
        {takeQ.data ? (
          <div className="space-y-3">
            <p className="m-0 text-[14px] text-[var(--text)]">
              {takeQ.data.line_text ?? (
                <em className="text-[var(--text-3)]">{t(copy.common.EMPTY.no_line_text)}</em>
              )}
            </p>
            <TakeAudioPlayer audioUrl={takeQ.data.audio_url} />
            <QcMetrics metrics={takeQ.data.qc_metrics} />
            {takeQ.data.supervisor_notes ? (
              <p className="m-0 text-[12px] text-[var(--text-2)]">
                <strong>Notes: </strong>
                {takeQ.data.supervisor_notes}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="m-0 text-[13px] text-[var(--text-2)]">Loading take…</p>
        )}
      </Modal>

      <Modal
        open={pickupForId != null}
        onClose={() => setPickupForId(null)}
        title="Request pickup"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPickupForId(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onSubmitPickup()}
              loading={decide.isPending}
            >
              Send to actor
            </Button>
          </>
        }
      >
        <p className="mb-2 text-[13px] text-[var(--text-2)]">
          Tell the actor what to redo. They will see this note before recording.
        </p>
        <Textarea
          aria-label="Pickup note"
          rows={4}
          value={pickupNote}
          onChange={(e) => setPickupNote(e.target.value)}
          placeholder="e.g. Try a softer read on the second half — the line should land on the cue."
        />
      </Modal>
    </section>
  )
}
